import { Component, inject, OnInit, effect } from '@angular/core';
import { MeetingService } from '../meeting.service';
import { Meeting, MEETING_STATUS } from '@/types/meeting.types';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { getMeetingListDataTableStructure } from './utils/meeting-list-data-table-object';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableAction, DataTableRowActions } from '@/components/data-table-builder/data-table.types';
import { Router } from '@angular/router';
import { UserRole } from '@/types/user.types';
import { AuthorizationService } from '@/services/authorization.service';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-meetings-list',
  imports: [PageHeaderComponent, DataTableBuilderComponent],
  templateUrl: './meetings-list.component.html',
  styleUrl: './meetings-list.component.scss',
})
export class MeetingsListComponent implements OnInit {
  meetingService = inject(MeetingService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);
  authService = inject(AuthorizationService);

  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.meetingsList',
      routerLink: ['/meetings', 'list'],
    },
  ];

  data: Meeting[] = [];
  loading = true;
  roles: string[] = [];

  totalRecords = 0;
  serverQuery = createServerQuery({});

  rowActionsFn: (target: Meeting) => DataTableRowActions = (target: Meeting) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};

    additionalActions['meetingActions'] = [];

    if ([MEETING_STATUS.PLANIFIER, MEETING_STATUS.EN_RETARD].includes(target.status) && new Date(target.reunionDate) <= new Date()) {
      additionalActions['meetingActions'].push({
        icon: 'pi pi-play-circle',
        label: 'meeting.actions.launch',
        action: () => this.launchMeeting(target.id),
      });
    }
    if ([MEETING_STATUS.EN_COURS, MEETING_STATUS.VALIDER].includes(target.status)) {
      additionalActions['meetingActions'].push({
        icon: 'pi pi-pencil',
        label: 'meeting.actions.treat',
        action: () => this.router.navigate(['/meetings', 'convene', target.id]),
      });
    }

    if (
      [MEETING_STATUS.PLANIFIER, MEETING_STATUS.NON_PLANIFIER].includes(target.status) ||
      ([MEETING_STATUS.EN_COURS].includes(target.status) && target.canBeCancelled)
    ) {
      additionalActions['meetingActions'].push({
        icon: 'pi pi-stop',
        label: 'meeting.actions.cancel',
        action: () => this.cancelMeeting(target.id),
      });
    }

    if ([MEETING_STATUS.VALIDER, MEETING_STATUS.CLOTURER].includes(target.status)) {
      additionalActions['meetingActions'].push({
        icon: 'pi pi-eye',
        label: 'table.actions.inspect',
        action: () => this.router.navigate(['/meetings', 'inspect-convened', target.id]),
      });
    }

    return {
      editAction: ![MEETING_STATUS.ANNULER, MEETING_STATUS.CLOTURER, MEETING_STATUS.EN_COURS, MEETING_STATUS.VALIDER].includes(target.status)
        ? () => this.router.navigate(['/meetings', 'edit', target.id])
        : undefined,

      inspectAction: [MEETING_STATUS.ANNULER].includes(target.status) ? () => this.router.navigate(['/meetings', 'inspect', target.id]) : undefined,

      deleteAction: target.status === MEETING_STATUS.BROUILLON ? () => this.deleteMeeting(target.id) : undefined,
      additionalActions: additionalActions,
    };
  };

  dataTableObject = getMeetingListDataTableStructure({
    createAction: this.authService.hasRoles([
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN_CENTRAL,
      UserRole.CPMP_PLANIFICATEUR,
      UserRole.CNCMP_PER_PLANIFICATEUR,
      UserRole.CNCMP_SPE_PLANIFICATEUR,
    ])
      ? () => this.router.navigate(['/meetings', 'new'])
      : undefined,
    rowActionsFn: this.authService.hasRoles([
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN_CENTRAL,
      UserRole.CPMP_PLANIFICATEUR,
      UserRole.CNCMP_PER_PLANIFICATEUR,
      UserRole.CNCMP_SPE_PLANIFICATEUR,
      UserRole.CNCMP_PER_RAPPORTEUR,
      UserRole.CNCMP_SPE_RAPPORTEUR,
      UserRole.CPMP_RAPPORTEUR,
    ])
      ? this.rowActionsFn
      : undefined,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.roles = this.authService.getRoles();
  }

  loadData() {
    this.meetingService
      .getAll({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : 'reunionDate,desc',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = (data as any).totalElements ?? data.content.length;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('meeting.message.error.loading'),
          });
        },
      });
  }

  launchMeeting(id: number) {
    this.meetingService.startMeeting(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.launch'),
        });
        this.router.navigate(['/meetings', 'convene', id]);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('meeting.message.error.launch'),
        });
      },
    });
  }

  deleteMeeting(id: number) {
    this.meetingService.delete(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.deleted'),
        });
        this.loadData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('meeting.message.error.deleted'),
        });
      },
    });
  }

  cancelMeeting(id: number) {
    this.meetingService.cancel(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.cancelled'),
        });
        this.router.navigate(['/meetings']);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('meeting.message.error.cancelled'),
        });
      },
    });
  }
}
